import { useEffect, Fragment, useRef, useState } from "react";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";
import {
    CalendarIcon,
    CheckIcon,
    ClockIcon,
    PlusIcon,
    Cog6ToothIcon,
    MagnifyingGlassIcon,
    ChevronDownIcon,
    XMarkIcon,
    AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { DialogMenu } from "./components/DialogMenu";
import api from "../../api";
import { AppointmentForm } from "./components/AppointmentForm";
import { AppointmentTable } from "./components/AppointmentTable";
import { AppointmentListMobile } from "./components/AppointmentListMobile";
import { Menu, Transition } from "@headlessui/react";
import SimpleCombobox from "../../components/SimpleCombobox";
import { DrAppointmentTable } from "./components/DrAppointmentTable";
import toast, { Toaster } from "react-hot-toast";
import DayCalendar from "./components/DayCalendar";
import { useSchedule } from "../../context/ScheduleContext";
import { useMergedSchedule } from "../../hooks/scheduleHooks";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import SidebarToggleButton from "../../components/layout/SidebarToggleButton";

export function SchedulePage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const dialogRef = useRef(null);
    const editDialogRef = useRef(null);
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const {
        appointments,
        doctorSchedule,
        selectedDoctor,
        setSelectedDoctor,
        selectedDate,
        setSelectedDate,
        loadingAppointments,
        loadingSchedule,
        counter,
        fetchAppointments,
        fetchSchedule,
        setSearchTerm,
        searchAllDates,
        setSearchAllDates,
        setCounter,
    } = useSchedule();
    const mergedSchedule = useMergedSchedule(
        appointments,
        doctorSchedule,
        selectedDoctor,
        selectedDate,
    );
    const rowsToDisplay = selectedDoctor ? mergedSchedule : appointments;

    useEffect(() => {
        const totalAppointments = selectedDoctor
            ? rowsToDisplay.filter((a) => a.availability === "reservado")
                  .length
            : rowsToDisplay.length;
        const confirmedAppointments = rowsToDisplay.filter(
            (a) => a.status === 3,
        ).length;
        const availableSlots = doctorSchedule.length;

        setCounter({
            appointments: totalAppointments,
            confirm: confirmedAppointments,
            available: availableSlots,
        });
    }, [doctorSchedule, rowsToDisplay, selectedDoctor, setCounter]);

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop]);

    const notify = (message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
                padding: "16px",
                borderRadius: "8px",
            },
            duration: 4000,
        });
    };

    const handleRowClick = (appt) => {
        setSelectedAppointment(appt);
        editDialogRef.current?.showModal();
    };

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            currentScreen="Agendamiento"
            header={
                <LocalHeader
                    dialogRef={dialogRef}
                    setSelectedDate={setSelectedDate}
                    selectedDate={selectedDate}
                    setSelectedDoctor={setSelectedDoctor}
                    selectedDoctor={selectedDoctor}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    setSearchTerm={setSearchTerm}
                    isDesktop={isDesktop}
                    sidebarOpenDesktop={sidebarOpenDesktop}
                    setSidebarOpenDesktop={setSidebarOpenDesktop}
                    searchAllDates={searchAllDates}
                    setSearchAllDates={setSearchAllDates}
                />
            }
        >
                <main className="flex-1 space-y-6 py-6 px-4 sm:px-6 lg:px-8 pb-24 lg:pb-8">
                    {selectedDoctor && (
                        <button
                            onClick={() => setSelectedDoctor(null)}
                            className="px-4 py-2 rounded-full text-sm font-semibold flex justify-between items-center gap-1 text-blue-800 bg-blue-100"
                        >
                            {" "}
                            <span>{selectedDoctor.name}</span>
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    )}
                    <h2 className="text-4xl font-bold dark:text-gray-200">
                        Agendamientos
                    </h2>
                    <div className="flex flex-col justify-start md:flex-row gap-3 md:flex-wrap">
                        <div className="w-full sm:w-72 flex">
                            <DayCalendar
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                            />
                        </div>
                        <Card
                            icon={<CalendarIcon className="h-6 w-6" />}
                            iconBg={"bg-[#B2DDFF]"}
                            small={"Total de agendamientos"}
                            total={counter.appointments}
                        />
                        <Card
                            icon={<CheckIcon className="h-6 w-6" />}
                            iconBg={"bg-[#A6F4C5]"}
                            small={"Confirmados"}
                            total={counter.confirm}
                        />
                        {selectedDoctor && (
                            <Card
                                icon={<ClockIcon className="h-6 w-6" />}
                                iconBg={"bg-[#D9D6FE]"}
                                small={"Turnos disponibles"}
                                total={counter.available}
                            />
                        )}
                    </div>
                    <div className="flex flex-col md:flex-row gap-5">
                        <div className="w-full">
                            <div className="lg:hidden">
                                <AppointmentListMobile
                                    data={rowsToDisplay}
                                    onRowClick={handleRowClick}
                                    loading={
                                        selectedDoctor
                                            ? loadingAppointments || loadingSchedule
                                            : loadingAppointments
                                    }
                                    onSave={() => {
                                        fetchAppointments();
                                        fetchSchedule();
                                    }}
                                    notify={notify}
                                    doctorView={Boolean(selectedDoctor)}
                                />
                            </div>
                            <div className="w-full max-h-[660px] overflow-auto rounded relative hidden lg:block">
                                {selectedDoctor ? (
                                    <DrAppointmentTable
                                        data={rowsToDisplay}
                                        onRowClick={handleRowClick}
                                        loading={loadingAppointments || loadingSchedule}
                                        onSave={() => {
                                            fetchAppointments();
                                            fetchSchedule();
                                        }}
                                        notify={notify}
                                    />
                                ) : (
                                    <AppointmentTable
                                        data={rowsToDisplay}
                                        onRowClick={handleRowClick}
                                        loading={loadingAppointments}
                                        onSave={() => {
                                            fetchAppointments();
                                            fetchSchedule();
                                        }}
                                        notify={notify}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => dialogRef.current?.showModal()}
                        className="fixed bottom-5 right-5 z-20 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.02] lg:hidden"
                    >
                        <PlusIcon className="h-5 w-5" />
                        <span>Nuevo agendamiento</span>
                    </button>
                    <DialogMenu dialogRef={dialogRef}>
                        <AppointmentForm
                            dialogRef={dialogRef}
                            onSave={() => {
                                fetchAppointments();
                                fetchSchedule();
                            }}
                            selectedDoctor={selectedDoctor}
                            notify={notify}
                        />
                    </DialogMenu>
                    <DialogMenu dialogRef={editDialogRef}>
                        <AppointmentForm
                            dialogRef={editDialogRef}
                            initialAppointment={selectedAppointment}
                            onSave={() => {
                                fetchAppointments();
                                fetchSchedule();
                            }}
                            notify={notify}
                        />
                    </DialogMenu>
                </main>
            <Toaster />
        </PatientModuleLayout>
    );
}

// const ClosestAvailability = ({ dates }) => {
//     return (
//         <div className="p-3 text-gray-700 dark:text-gray-200">
//             <h3 className="text-center border-b dark:border-gray-700 p-3 text-gray-900 dark:text-white font-bold text-lg">
//                 Disponibilidad más cercana
//             </h3>
//             <div className="flex justify-between items-center border-b dark:border-gray-700 p-3">
//                 <span>Doctor</span>
//                 <span>Fecha</span>
//             </div>
//             <ul>
//                 {dates.map((date) => (
//                     <li
//                         key={date.id}
//                         className="flex justify-between items-center border-b dark:border-gray-700 px-3 py-2"
//                     >
//                         <span>{date.doctor}</span>
//                         <span>{date.date}</span>
//                     </li>
//                 ))}
//             </ul>
//         </div>
//     );
// };

const Card = ({ icon, iconBg, small, total }) => {
    return (
        <div className="flex flex-col gap-2 max-h-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 w-full sm:w-72">
            <div
                className={`${iconBg} rounded-lg w-10 h-10 flex items-center justify-center text-white`}
            >
                {icon}
            </div>
            <small className="text-gray-600 font-semibold">{small}</small>
            <span className="text-4xl font-bold">{total}</span>
        </div>
    );
};

const LocalHeader = ({
    dialogRef,
    selectedDate,
    setSelectedDate,
    setSelectedDoctor,
    selectedDoctor,
    setSidebarOpen,
    setSearchTerm,
    sidebarOpen,
    isDesktop,
    setSidebarOpenDesktop,
    sidebarOpenDesktop,
    searchAllDates,
    setSearchAllDates,
}) => {
    const [doctors, setDoctors] = useState([]);

    const fromInputDate = (value) => {
        if (!value) return null;
        const [y, m, d] = value.split("-").map(Number);
        return new Date(y, m - 1, d);
    };

    const handleChange = (field, value) => {
        void field;
        setSelectedDoctor(value);
    };

    useEffect(() => {
        api.doctors.getMulti().then((response) => {
            const data = response;
            const doctorsOptions = data.map((doc) => ({
                ...doc,
                name: `${doc.firstName} ${doc.lastName} ${doc.govId}`,
            }));
            setDoctors(doctorsOptions);
        });
    }, []);

    const handleSidebarToggle = () => {
        if (isDesktop) {
            setSidebarOpenDesktop(!sidebarOpenDesktop);
            return;
        }

        setSidebarOpen(!sidebarOpen);
    };

    return (
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
          <div className="flex min-h-[72px] items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <SidebarToggleButton onClick={handleSidebarToggle} />
                <div className="flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-slate-50 px-3 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <label htmlFor="search">
                        <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-primary" />
                    </label>
                    <input
                        type="search"
                        name="search"
                        id="search"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full border-none bg-transparent px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-0"
                        placeholder="Buscar"
                    />
                </div>
            </div>
            <div className="hidden xl:flex items-center justify-end gap-3">
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 px-3 text-sm">
                        <input
                            type="checkbox"
                            checked={searchAllDates}
                            onChange={(e) =>
                                setSearchAllDates(e.target.checked)
                            }
                        />
                        Buscar en todas las fechas
                    </label>
                    <Menu as="div" className="relative inline text-left">
                        <div>
                            <Menu.Button className="inline-flex items-center w-full justify-center gap-x-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                                    Médico
                                    <ChevronDownIcon
                                        className="-mr-1 h-5 w-5"
                                        aria-hidden="true"
                                    />
                                </Menu.Button>
                            </div>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="absolute z-10 mt-2 w-64 origin-top-right rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-900">
                                    <div className="py-4 flex flex-col gap-3 px-5">
                                        <SimpleCombobox
                                            inputClassName="relative mt-1"
                                            placeholder={"Buscar"}
                                            handleChange={handleChange}
                                            name="doctor"
                                            label="Doctor"
                                            labelClassname="font-semibold"
                                            value={selectedDoctor}
                                            options={doctors}
                                        />
                                    </div>
                                </Menu.Items>
                            </Transition>
                    </Menu>
                    <Link
                        to="/schedule/agenda"
                        className="items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 inline-flex"
                    >
                        <Cog6ToothIcon className="h-5 w-5" />
                        <span>Configurar agenda</span>
                    </Link>
                    <button
                        className="items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm inline-flex"
                        onClick={() => dialogRef.current?.showModal()}
                    >
                        <PlusIcon className="h-5 w-5" />
                        <span>Nuevo Agendamiento</span>
                    </button>
                </div>
            </div>
            <div className="xl:hidden flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => dialogRef.current?.showModal()}
                    className="hidden lg:inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white shadow-sm"
                    title="Nuevo agendamiento"
                >
                    <PlusIcon className="h-5 w-5" />
                </button>
                <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="inline-flex items-center justify-center p-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <AdjustmentsHorizontalIcon className="h-5 w-5" />
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-2rem))] flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-900">
                            {/* Fecha */}
                            <div>
                                <label
                                    htmlFor="date"
                                    className="text-sm font-medium"
                                >
                                    Fecha
                                </label>
                                <input
                                    type="date"
                                    name="date"
                                    id="date"
                                    value={
                                        selectedDate
                                            ? selectedDate
                                                  .toISOString()
                                                  .split("T")[0]
                                            : ""
                                    }
                                    onChange={(e) =>
                                        setSelectedDate(
                                            fromInputDate(e.target.value)
                                        )
                                    }
                                    className="input-base mt-1 w-full"
                                />
                            </div>

                            {/* Doctor */}
                            <div>
                                <label className="text-sm font-medium">
                                    Médico
                                </label>
                                <SimpleCombobox
                                    inputClassName="relative mt-1 w-full"
                                    placeholder={"Buscar"}
                                    handleChange={handleChange}
                                    name="doctor"
                                    label="Doctor"
                                    value={selectedDoctor}
                                    options={doctors}
                                />
                            </div>
                            <label className="flex gap-2 items-center text-sm">
                                <input
                                    type="checkbox"
                                    checked={searchAllDates}
                                    onChange={(e) =>
                                        setSearchAllDates(e.target.checked)
                                    }
                                />
                                Buscar en todas las fechas
                            </label>

                            {/* Configuración */}
                            <Link
                                to="/schedule/agenda"
                                className="items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 inline-flex"
                            >
                                <Cog6ToothIcon className="h-5 w-5" />
                                <span>Configurar agenda</span>
                            </Link>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
          </div>
        </div>
    );
};
